import { Box, Typography, useTheme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, OVERTIME, STATUS } from "@/theme/tokens";
import { JumpingDots } from "@/ui/Animations/JumpingDots";
import { EyebrowLabel } from "@/ui/molecules/EyebrowLabel";
import { FocalCard } from "@/ui/molecules/FocalCard";
import { formatPredictedCost } from "@/utils/costPrediction";
import type { BaseRequest } from "@/utils/models";
import { EMPLOYMENT_TYPE_LABEL_KEY } from "@/utils/utilFunctions";
import { GapsRoleBadge } from "../GapsRoleBadge";
import { currentRequestBonus } from "../gapsPickupBonuses";
import type { GapsAcceptedHours } from "../openShifts/gapsOpenShiftsModel";
import type { GapsApplicant } from "../openShifts/useGapsApplicants";
import { useGapsRequestLabels } from "../openShifts/useGapsRequestLabels";
import { GapsJobBadge } from "./GapsJobBadge";

type Props = {
	request: BaseRequest;
	/** The person, once the roster lookup has them; `undefined` for a responder no longer on the roster. */
	applicant: GapsApplicant | undefined;
	/**
	 * A partial pickup, by the approve write's own test (`isPartialShift`) — which decides both the hours it writes and
	 * that it writes no bonus. Passed rather than read off `applicant`, so it holds for anyone the roster no longer lists.
	 */
	isPartial: boolean;
	/** The hours the approval writes (`acceptedHoursOf`). */
	hours: GapsAcceptedHours;
	/** The backend's overtime for THESE hours — a partial pickup's own, not the posted shift's. `undefined` = not known. */
	overtimeHours: number | undefined;
	isOvertimeLoading: boolean;
	dataTestId: string;
};

const valueSx = { m: 0, fontSize: FONT_SIZE.SECONDARY, color: INK.PRIMARY, fontVariantNumeric: "tabular-nums" } as const;

/**
 * What approving this person commits to, stated before the write — the confirm sheet's one focal card.
 *
 * Every line is a fact the approval already rests on: the hours it writes (their accepted part for a partial pickup),
 * the overtime the backend predicts for them, their other shift that day, agency staff, and the pick-up bonus the new
 * shift is written with (`approveAddRequest`: the request's current bonus, none for a partial pickup). Nothing here is
 * calculated on the phone.
 */
export const GapsApplicantFacts: FC<Props> = ({ request, applicant, isPartial, hours, overtimeHours, isOvertimeLoading, dataTestId }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const { hoursLabel } = useGapsRequestLabels();
	// `approveAddRequest`: a partial pickup is written with no bonus, a whole-shift one with the request's current bonus.
	const bonus = isPartial ? 0 : currentRequestBonus(request);
	const otherShift = applicant?.otherShift;

	const facts: { key: string; term: string; value: ReactNode }[] = [
		{
			key: "hours",
			term: t("gaps.approve.factHours"),
			value: isPartial
				? t("gaps.approve.partShift", { hours: hoursLabel(hours.start, hours.end) })
				: t("gaps.approve.wholeShift", { hours: hoursLabel(hours.start, hours.end) }),
		},
		{
			key: "overtime",
			term: t("gaps.approve.factOvertime"),
			value: isOvertimeLoading ? (
				<JumpingDots />
			) : overtimeHours !== undefined && overtimeHours > 0 ? (
				<Box component="span" sx={{ fontWeight: 700, color: OVERTIME.INK }}>
					{t("gaps.approve.overtime", { hours: Number(overtimeHours.toFixed(1)) })}
				</Box>
			) : overtimeHours === undefined ? (
				t("gaps.approve.overtimeUnknown")
			) : (
				t("gaps.approve.noOvertime")
			),
		},
		{
			key: "day",
			term: t("gaps.approve.factThatDay"),
			// "No other shift" only for a day the phone holds: a request outside the loaded weeks (or a responder the
			// roster lookup has not reached) is "not known", as the overtime fact says for a prediction it does not have.
			value:
				otherShift === undefined || !otherShift.isKnown ? (
					t("gaps.approve.thatDayUnknown")
				) : otherShift.shift === undefined ? (
					t("gaps.approve.nothingElse")
				) : (
					<Box component="span" sx={{ fontWeight: 600, color: STATUS.WARNING.dark }}>
						{t("gaps.approve.alsoOn", { hours: hoursLabel(otherShift.shift.startTime, otherShift.shift.endTime) })}
					</Box>
				),
		},
		...(bonus > 0 ? [{ key: "bonus", term: t("gaps.approve.factBonus"), value: t("gaps.approve.bonus", { amount: formatPredictedCost(bonus) }) }] : []),
	];

	return (
		<FocalCard ink={theme.palette.primary.main} dataTestId={dataTestId} sx={{ mx: 2, mt: 2, mb: 1.5, px: 1.75, py: 1.5 }}>
			{applicant !== undefined && (
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
					{applicant.job !== undefined ? (
						<GapsJobBadge group={{ jobLabel: applicant.job.displayName, isJobSection: true, jobId: applicant.job.id }} dense />
					) : (
						applicant.nurse !== undefined && <GapsRoleBadge role={applicant.nurse.role} size="row" />
					)}
					<Typography sx={{ flex: 1, minWidth: 0, fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700, color: INK.PRIMARY, overflowWrap: "anywhere" }}>
						{applicant.name}
					</Typography>
					{applicant.nurse !== undefined && (
						<Typography
							component="span"
							data-testid={`${dataTestId}-employment`}
							sx={{
								flex: "none",
								fontSize: FONT_SIZE.CAPTION,
								color: applicant.isAgency ? STATUS.INFO.dark : INK.MUTED,
								fontWeight: applicant.isAgency ? 700 : 400,
							}}
						>
							{applicant.isAgency ? t("gaps.requests.agency") : t(EMPLOYMENT_TYPE_LABEL_KEY[applicant.nurse.employmentType])}
						</Typography>
					)}
				</Box>
			)}
			<Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1.75, rowGap: 0.75 }}>
				{facts.map((fact) => (
					<Box key={fact.key} sx={{ display: "contents" }} data-testid={`${dataTestId}-${fact.key}`}>
						<EyebrowLabel component="dt" sx={{ alignSelf: "center" }}>
							{fact.term}
						</EyebrowLabel>
						<Box component="dd" sx={valueSx}>
							{fact.value}
						</Box>
					</Box>
				))}
			</Box>
		</FocalCard>
	);
};
